import React, { forwardRef, MouseEvent } from "react";
import cx from "classnames";
import { useNavigate } from "react-router-dom";

export type ButtonProps = Omit<JSX.IntrinsicElements["button"], "onClick"> & {
  kind?: "primary" | "secondary" | "default";
  large?: boolean;
  loading?: boolean;
  helperText?: string;
  to?: string | null;
  onClick?(event: MouseEvent<HTMLButtonElement>): void | Promise<void>;
};

const Button = (
  {
    className,
    to = null,
    type = "button",
    kind = "default",
    children,
    onClick,
    onMouseEnter,
    disabled,
    loading,
    large,
    helperText,
    ...rest
  }: ButtonProps,
  ref?: React.Ref<HTMLButtonElement>
) => {
  const navigate = useNavigate();
  const isDisabled = disabled || loading;

  const wrapper = "flex flex-col justify-center	items-center";
  const helperTextClasses = "text-xs text-tim-blue_08c opacity-50";

  const primaryClasses = [
    "text-white focus:ring-2 focus:ring-tim-green/40 focus:ring-offset-2",
    !isDisabled &&
      "bg-tim-green hover:bg-tim-greenHover active:scale-[0.98]",
    isDisabled && loading && "",
  ];

  const secondaryClasses = [
    "text-tim-blue_08c hover:text-tim-primary focus:ring-0",
  ];

  const defaultClasses = ["text-white", !isDisabled && "bg-tim-default_blue hover:opacity-90"];

  const largeClasses = ["w-full"];
  const disabledClasses = [
    "cursor-not-allowed",
    kind !== "secondary" && "bg-gray-300 text-gray-500",
  ];
  const classNames = cx(
    className,
    "text-sm font-medium transition-all duration-200 ease-out",
    "py-3 min-h-11 px-6 rounded-xl focus:outline-none relative",
    kind === "primary" && primaryClasses,
    kind === "secondary" && secondaryClasses,
    kind === "default" && defaultClasses,
    large && largeClasses,
    isDisabled && disabledClasses
  );

  const handleOnClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isDisabled) {
      event.preventDefault();
      return;
    }
    await onClick?.(event);
    if (to) {
      navigate(to);
    }
  };

  return (
    <div className={wrapper}>
      <button
        ref={ref}
        className={classNames}
        type={type}
        disabled={isDisabled}
        onClick={handleOnClick}
        onMouseEnter={(e) => {
          if (onMouseEnter) {
            onMouseEnter(e);
          }
        }}
        {...rest}
      >
        <div className="mb-px pb-px pt-1 leading-none">{children}</div>
        {loading && (
          <div className="formInput__loaderContainer absolute bottom-0 flex h-full items-center text-tim-primary">
            <i className="formInput__loader text-white" />
          </div>
        )}
      </button>
      {helperText && <span className={helperTextClasses}>{helperText}</span>}
    </div>
  );
};

export default forwardRef(Button);
